import { cloneDeep, isNil } from "lodash-es";
import type { FieldConfig } from "../fields/types";
import type { FieldConfigDto, SubformDto } from "../services/ApiService";
import type { Favourite } from "../services/FavouriteService";
import {
  type FormScope,
  type ReferenceValueAction,
  type Rule,
  RuleActionKey,
  type Subform,
} from "../state/formVersion";
import { toRule } from "./mappers/rule";
import SubformId from "../state/SubformId";
import { toField, widgetUidToField } from "../fields/fields";
import FieldId from "../fields/FieldId";
import type { CalculationFieldProperties } from "../fields/number/CalculationField";
import type VariableValueReference from "../variables/VariableValueReference";
import FieldValueReference from "../fields/FieldValueReference";
import { toSubformEmailConfiguration } from "./mappers/subformEmailConfiguration";
import type { TimeDifferenceFieldProperties } from "../fields/dateTime/TimeDifferenceField";
import type { SubformFieldProperties } from "../fields/subform/SubformField";
import type { StringTemplate } from "./stringTemplateUtil";
import logger from "./logger";
import FormServicesFactory from "../services/FormServicesFactory";
import type { PinFieldProperties } from "../fields/media/PinField";

type PinDto = {
  form?: SubformDto;
  name?: string;
  target_form_id?: SubformId;
};

const mapFieldValueReference = (
  fieldValueReference: FieldValueReference,
  fieldMap: Map<string, string>,
): FieldValueReference => {
  const mappedFieldValueReference = fieldMap.get(fieldValueReference.toString());

  if (isNil(mappedFieldValueReference)) {
    // This is a reference to a normal field (not inside a subform), and does not need to be remapped.
    // `fromDto` in `toFavourite` fails to type them correctly, though, so we need to ensure the type here.
    return fieldValueReference instanceof FieldValueReference
      ? fieldValueReference
      : FieldValueReference.from(fieldValueReference);
  }
  return FieldValueReference.from(mappedFieldValueReference);
};

const mapFieldId = (fieldId: FieldId, fieldMap: Map<string, string>): FieldId => {
  const mappedFieldId = fieldMap.get(fieldId.toString());

  if (isNil(mappedFieldId)) {
    logger.error("Failed to map field id inside a favourite");
    return fieldId;
  }
  return FieldId.from(mappedFieldId);
};

const mapIdsOfStringTemplate = (stringTemplate: StringTemplate, fieldMap: Map<string, string>): StringTemplate =>
  stringTemplate.map((value) => {
    if (value.type === "field") {
      return { ...value, value: mapFieldValueReference(value.value, fieldMap) };
    }
    return value;
  });

const toFieldMap = (fields: FieldConfig[]) =>
  fields.reduce((acc, field) => {
    acc.set(field.uid.toString(), field);
    return acc;
  }, new Map<string, FieldConfig>());

const mapField = (fieldDto: FieldConfigDto, formScope: FormScope, domainObjects: DomainObjectsWithIdMap): void => {
  const field = widgetUidToField(fieldDto.widget);
  const additionalProps = field.fromDto(fieldDto.properties) as Record<string, unknown>;

  if (
    fieldDto.widget === "com.moreapps:detail:1" &&
    !isNil((additionalProps as unknown as { form: SubformDto; target_form_id?: SubformId })?.form)
  ) {
    const subformDto = additionalProps.form as SubformDto;
    const subformId = mapSubformDto(
      subformDto,
      domainObjects,
      subformDto.meta?.name ?? (additionalProps?.label_text as string) ?? "",
    );
    additionalProps.target_form_id = subformId;
  } else if (
    fieldDto.widget === "com.moreapps:pin:1" &&
    !isNil((additionalProps as unknown as { pins: PinDto[] })?.pins)
  ) {
    (additionalProps.pins as PinDto[]).forEach((pin) => {
      if ("form" in pin) {
        const subformDto = pin.form as SubformDto;
        const subformId = mapSubformDto(
          subformDto,
          domainObjects,
          subformDto.meta?.name ?? (pin?.name as string) ?? "",
        );

        delete pin.form;

        pin.target_form_id = subformId;
      }
    });
  }
  if ("form" in additionalProps) {
    delete additionalProps.form;
  }

  const fieldConfig = field.init(formScope, additionalProps);
  domainObjects.fields = [...domainObjects.fields, fieldConfig];
  domainObjects.idMap.set(fieldDto.uid, fieldConfig.uid.toString());
};

const mapSubformDto = (subformDto: SubformDto, domainObjects: DomainObjectsWithIdMap, label: string): SubformId => {
  // Return existing subform when subform already exists in favourite context.
  const existingSubformId = domainObjects.idMap.get(subformDto.uid);
  if (!isNil(existingSubformId)) {
    return SubformId.from(existingSubformId);
  }

  const subform = {
    uid: SubformId.random(),
    meta: {
      name: label ?? "",
    },
  };

  if (!isNil(subformDto.fields)) {
    subformDto.fields
      .filter((field) => !isNil(field))
      .forEach((field) => {
        mapField(field, subform.uid, domainObjects);
      });
  }

  domainObjects.subforms = [
    ...domainObjects.subforms,
    {
      ...subform,
      emailConfiguration: toSubformEmailConfiguration(
        subformDto,
        domainObjects.fields.filter((field) => subform.uid.equals(field.scope.toString())),
        [],
      ),
    },
  ];
  domainObjects.idMap.set(subformDto.uid, subform.uid.toString());

  if (!isNil(subformDto.rules) && subformDto.rules?.length > 0) {
    const fieldMap = toFieldMap(domainObjects.fields);
    const rules: Rule[] = subformDto.rules.map((ruleDto) => toRule(ruleDto, fieldMap, new Map(), subform.uid));
    domainObjects.rules = [...domainObjects.rules, ...rules];
  }

  return subform.uid;
};

export type DomainObjects = {
  fields: FieldConfig<Record<string, unknown>>[];
  subforms: Subform[];
  rules: Rule[];
};

type DomainObjectsWithIdMap = DomainObjects & {
  idMap: Map<string, string>;
};

export const mapFavouriteToDomainObjects = (favourite: Favourite, formScope: FormScope): DomainObjects => {
  const domainObjects: DomainObjectsWithIdMap = {
    fields: [],
    subforms: [],
    rules: [],
    idMap: new Map<string, string>(),
  };

  const additionalProps = cloneDeep(favourite.changedProps) as Record<string, unknown>;

  if (favourite.field.type === "subform" && !isNil(additionalProps.form)) {
    const subformDto = additionalProps.form as SubformDto;
    const subformId = mapSubformDto(
      subformDto,
      domainObjects,
      subformDto.meta?.name ?? (additionalProps?.label_text as string) ?? "",
    );

    delete additionalProps.form;
    additionalProps.target_form_id = subformId;
  } else if (favourite.field.type === "pin" && !isNil((additionalProps as { pins: unknown[] })?.pins)) {
    (additionalProps.pins as PinDto[]).forEach((pin) => {
      if ("form" in pin) {
        const subformDto = pin.form as SubformDto;
        const subformId = mapSubformDto(
          subformDto,
          domainObjects,
          subformDto.meta?.name ?? (pin?.name as string) ?? "",
        );

        delete pin.form;
        pin.target_form_id = subformId;
      }
    });
  }

  const { fieldService } = FormServicesFactory.forScope(formScope);
  const fieldConfig = fieldService.createField(favourite.field.type, additionalProps);
  domainObjects.fields = [...domainObjects.fields, fieldConfig];
  domainObjects.idMap.set(favourite.id, fieldConfig.uid.toString());

  domainObjects.rules.forEach((rule) => {
    rule.conditions.forEach((condition) => {
      if (condition.type === "FIELD" || condition.type === "FIELD_DATA_SOURCE") {
        condition.fieldId = mapFieldId(condition.fieldId, domainObjects.idMap);
      }
    });

    rule.actions.forEach((action) => {
      if (!isNil(action.fieldId)) {
        action.fieldId = mapFieldId(action.fieldId, domainObjects.idMap);
      }

      if (!isNil((action as ReferenceValueAction).value) && action?.key === RuleActionKey.REFERENCE) {
        if (!isNil(action.value)) {
          action.value = mapFieldId(action.value as FieldId, domainObjects.idMap);
        }
      }
    });
  });

  domainObjects.fields.forEach((fieldConfig) => {
    const field = toField(fieldConfig.type);

    fieldConfig.properties = field.postProcessFromDto(
      fieldConfig.properties,
      domainObjects.fields,
      [],
      fieldConfig.scope,
    ) as Record<string, unknown>;

    if (fieldConfig.type === "subform" && !isNil((fieldConfig.properties as SubformFieldProperties).itemHtml)) {
      (fieldConfig.properties as SubformFieldProperties).itemHtml = mapIdsOfStringTemplate(
        (fieldConfig.properties as SubformFieldProperties).itemHtml,
        domainObjects.idMap,
      );
    } else if (fieldConfig.type === "pin" && !isNil((fieldConfig.properties as PinFieldProperties).pins)) {
      fieldConfig.properties.pins = (fieldConfig.properties as PinFieldProperties).pins.map((pin) => {
        if (!isNil(pin.itemMarkup)) {
          pin.itemMarkup = mapIdsOfStringTemplate(pin.itemMarkup, domainObjects.idMap);
        }
        return pin;
      });
    } else if (fieldConfig.type === "calculation" && !isNil(fieldConfig.properties.terms)) {
      const updatedTerms: (FieldValueReference | VariableValueReference)[] = [];

      (fieldConfig as FieldConfig<CalculationFieldProperties>).properties.terms.forEach((term) => {
        updatedTerms.push(mapFieldValueReference(term as FieldValueReference, domainObjects.idMap));
      });

      (fieldConfig as FieldConfig<CalculationFieldProperties>).properties.terms = updatedTerms;
    } else if (fieldConfig.type === "timeDifference") {
      if (!isNil((fieldConfig as FieldConfig<TimeDifferenceFieldProperties>).properties.start)) {
        (fieldConfig as FieldConfig<TimeDifferenceFieldProperties>).properties.start = mapFieldValueReference(
          (fieldConfig as FieldConfig<TimeDifferenceFieldProperties>).properties.start as FieldValueReference,
          domainObjects.idMap,
        );
      }

      if (!isNil((fieldConfig as FieldConfig<TimeDifferenceFieldProperties>).properties.end)) {
        (fieldConfig as FieldConfig<TimeDifferenceFieldProperties>).properties.end = mapFieldValueReference(
          (fieldConfig as FieldConfig<TimeDifferenceFieldProperties>).properties.end as FieldValueReference,
          domainObjects.idMap,
        );
      }
    }
  });

  delete (domainObjects as Partial<DomainObjectsWithIdMap>).idMap;
  return domainObjects;
};
